import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Select,
  Input,
  Button,
  Text,
  Checkbox,
} from '@chakra-ui/react';
import { useToasts } from 'react-toast-notifications';
import { useForm, Controller } from 'react-hook-form';
import {
  createDevice,
  updateDevice,
  GetAllPorts,
  getAllGroups,
} from '../api/side'; // Import the function here
import { getApiErrorMessage } from '../utils/apiError';

const ConnectionTypes = ['modbusTCP', 'modbusRTU', 'VirtualDevice'];

const ModalComponent = ({ isOpen, onClose, side, refetch }) => {
  const { addToast } = useToasts();
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      connType: '',
      deviceName: '',
      description: '',
      period: '',
      isEnabled: false,
      group_id: null,
      tcpModel: {
        ip: '',
        port: '',
      },
      rtuModel: {
        baudrate: '',
        com_port: '',
        data_bit: '',
        parity: '',
        slave_id: '',
        stop_bit: '',
      },
    },
    mode: 'onChange',
  });

  const [comPorts, setComPorts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isEditing, setIsEditing] = useState(!!side?.id);

  const watchedValues = watch();

  useEffect(() => {
    if (isOpen) {
      setIsEditing(!!side?.id);
      reset(
        side || {
          connType: '',
          deviceName: '',
          description: '',
          period: '',
          isEnabled: false,
          group_id: null,
          tcpModel: {
            ip: '',
            port: '',
          },
          rtuModel: {
            baudrate: '',
            com_port: '',
            data_bit: '',
            parity: '',
            slave_id: '',
            stop_bit: '',
          },
        }
      );

      const fetchComPorts = async () => {
        try {
          const ports = await GetAllPorts();
          setComPorts(ports);
        } catch (error) {
          console.error('Failed to fetch COM ports:', error);
        }
      };

      const fetchGroups = async () => {
        const groupsData = await getAllGroups();
        setGroups(groupsData);
      };

      fetchComPorts();
      fetchGroups();
    }
  }, [side, isOpen, reset]);

  const onSubmit = async data => {
    try {
      let response;
      if (data.connType === 'VirtualDevice') {
        // Handle Virtual Device
        const virtualDeviceData = {
          isEnabled: data.isEnabled,
          name: data.deviceName,
          description: data.description,
          group_id: data.group_id,
        };
        response = isEditing
          ? await updateDevice(side.id, virtualDeviceData, 'VirtualDevice')
          : await createDevice(virtualDeviceData, 'VirtualDevice');
      } else {
        // Handle modbusTCP and modbusRTU
        const deviceData = {
          connType: data.connType,
          description: data.description,
          period: parseInt(data.period, 10),
          deviceName: data.deviceName,
          isEnabled: data.isEnabled,
          timeout: 2000,
          group_id: data.group_id,
        };

        if (data.connType === 'modbusTCP') {
          deviceData.tcpModel = {
            ip: data.tcpModel.ip,
            port: parseInt(data.tcpModel.port, 10),
          };
          response = isEditing
            ? await updateDevice(side.id, deviceData, 'modbusTCP')
            : await createDevice(deviceData, 'modbusTCP');
        } else if (data.connType === 'modbusRTU') {
          deviceData.rtuModel = {
            baudrate: parseInt(data.rtuModel.baudrate, 10),
            com_port: data.rtuModel.com_port,
            data_bit: parseInt(data.rtuModel.data_bit, 10),
            parity: parseInt(data.rtuModel.parity, 10),
            slave_id: parseInt(data.rtuModel.slave_id, 10),
            stop_bit: parseInt(data.rtuModel.stop_bit, 10),
          };
          response = isEditing
            ? await updateDevice(side.id, deviceData, 'modbusRTU')
            : await createDevice(deviceData, 'modbusRTU');
        }
      }

      if (response && response.status === 200) {
        await refetch();
        addToast(`Device Successfully ${isEditing ? 'Updated' : 'Created'}`, {
          appearance: 'success',
          autoDismiss: true,
        });
        onClose();
      } else {
        throw new Error('Failed to process request');
      }
    } catch (error) {
      console.error(error.message);
      addToast(
        `Failed to ${isEditing ? 'Update' : 'Create'} Device: ${getApiErrorMessage(
          error,
          'Unknown error'
        )}`,
        {
          appearance: 'error',
          autoDismiss: true,
        }
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditing ? 'Edit Device' : 'Add New Device'}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Flex align="center">
              <Text minWidth="120px">Device Name:</Text>
              <Controller
                name="deviceName"
                control={control}
                rules={{ required: 'Device Name is required' }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Device Name"
                    variant="flushed"
                    required
                  />
                )}
              />
              {errors.deviceName && (
                <Text color="red.500">{errors.deviceName.message}</Text>
              )}
            </Flex>
            <Flex align="center">
              <Text minWidth="120px">Description:</Text>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Description"
                    variant="flushed"
                  />
                )}
              />
            </Flex>
            <Flex direction="column" gap="4">
              <Flex align="center">
                <Text minWidth="120px">Connection Type:</Text>
                <Controller
                  name="connType"
                  control={control}
                  rules={{ required: 'Connection Type is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select Connection Type"
                      variant="flushed"
                      required
                      disabled={isEditing}
                    >
                      {ConnectionTypes.map(type => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                {errors.connType && (
                  <Text color="red.500">{errors.connType.message}</Text>
                )}
              </Flex>

              <Flex align="center">
                <Text minWidth="120px">Group:</Text>
                <Controller
                  name="group_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select Group"
                      variant="flushed"
                      required={!isEditing}
                    >
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                {errors.group_id && (
                  <Text color="red.500">{errors.group_id.message}</Text>
                )}
              </Flex>

              {watchedValues.connType === 'modbusTCP' && (
                <>
                  <Flex align="center">
                    <Text minWidth="120px">IP:</Text>
                    <Controller
                      name="tcpModel.ip"
                      control={control}
                      rules={{
                        required: 'IP Address is required',
                        pattern: {
                          value:
                            /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                          message: 'Invalid IP Address',
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="IP Address"
                          variant="flushed"
                          required
                        />
                      )}
                    />
                    {errors.tcpModel?.ip && (
                      <Text color="red.500">{errors.tcpModel.ip.message}</Text>
                    )}
                  </Flex>
                  <Flex align="center">
                    <Text minWidth="120px">Port:</Text>
                    <Controller
                      name="tcpModel.port"
                      control={control}
                      rules={{ required: 'Port is required' }}
                      render={({ field }) => (
                        <Input
                          type="text"
                          pattern="\d*"
                          {...field}
                          placeholder="Port"
                          variant="flushed"
                          required
                        />
                      )}
                    />
                    {errors.tcpModel?.port && (
                      <Text color="red.500">
                        {errors.tcpModel.port.message}
                      </Text>
                    )}
                  </Flex>
                </>
              )}

              {watchedValues.connType === 'modbusRTU' && (
                <>
                  <Flex align="center">
                    <Text minWidth="120px">Slave ID:</Text>
                    <Controller
                      name="rtuModel.slave_id"
                      control={control}
                      rules={{ required: 'Slave ID is required' }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select Slave ID"
                          variant="flushed"
                          required
                        >
                          {[...Array(10).keys()].map(i => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.rtuModel?.slave_id && (
                      <Text color="red.500">
                        {errors.rtuModel.slave_id.message}
                      </Text>
                    )}
                  </Flex>
                  <Flex align="center">
                    <Text minWidth="120px">Baudrate:</Text>
                    <Controller
                      name="rtuModel.baudrate"
                      control={control}
                      rules={{ required: 'Baudrate is required' }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select Baudrate"
                          variant="flushed"
                          required
                        >
                          <option value="1200">1200</option>
                          <option value="2400">2400</option>
                          <option value="4800">4800</option>
                          <option value="9600">9600</option>
                          <option value="14400">14400</option>
                          <option value="19200">19200</option>
                          <option value="38400">38400</option>
                          <option value="57600">57600</option>
                        </Select>
                      )}
                    />
                    {errors.rtuModel?.baudrate && (
                      <Text color="red.500">
                        {errors.rtuModel.baudrate.message}
                      </Text>
                    )}
                  </Flex>
                  <Flex align="center">
                    <Text minWidth="120px">Data Bit:</Text>
                    <Controller
                      name="rtuModel.data_bit"
                      control={control}
                      rules={{ required: 'Data Bit is required' }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select Data Bit"
                          variant="flushed"
                          required
                        >
                          <option value="7">7</option>
                          <option value="8">8</option>
                        </Select>
                      )}
                    />
                    {errors.rtuModel?.data_bit && (
                      <Text color="red.500">
                        {errors.rtuModel.data_bit.message}
                      </Text>
                    )}
                  </Flex>
                  <Flex align="center">
                    <Text minWidth="120px">Stop Bit:</Text>
                    <Controller
                      name="rtuModel.stop_bit"
                      control={control}
                      rules={{ required: 'Stop Bit is required' }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select Stop Bit"
                          variant="flushed"
                          required
                        >
                          <option value="0">0</option>
                          <option value="1">1</option>
                        </Select>
                      )}
                    />
                    {errors.rtuModel?.stop_bit && (
                      <Text color="red.500">
                        {errors.rtuModel.stop_bit.message}
                      </Text>
                    )}
                  </Flex>
                  <Flex align="center">
                    <Text minWidth="120px">Parity:</Text>
                    <Controller
                      name="rtuModel.parity"
                      control={control}
                      rules={{ required: 'Parity is required' }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select Parity"
                          variant="flushed"
                          required
                        >
                          <option value="0">None</option>
                          <option value="1">Odd</option>
                          <option value="2">Even</option>
                        </Select>
                      )}
                    />
                    {errors.rtuModel?.parity && (
                      <Text color="red.500">
                        {errors.rtuModel.parity.message}
                      </Text>
                    )}
                  </Flex>
                  <Flex align="center">
                    <Text minWidth="120px">COM Port:</Text>
                    <Controller
                      name="rtuModel.com_port"
                      control={control}
                      rules={{ required: 'COM Port is required' }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select COM Port"
                          variant="flushed"
                          required
                        >
                          {comPorts.map((port, index) => (
                            <option key={index} value={port}>
                              {port}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.rtuModel?.com_port && (
                      <Text color="red.500">
                        {errors.rtuModel.com_port.message}
                      </Text>
                    )}
                  </Flex>
                </>
              )}

              {watchedValues.connType !== 'VirtualDevice' && (
                <Flex align="center">
                  <Text minWidth="120px">Period (sn):</Text>
                  <Controller
                    name="period"
                    control={control}
                    rules={{ required: 'Period is required' }}
                    render={({ field }) => (
                      <Input
                        type="number"
                        {...field}
                        placeholder="Period (sn)"
                        variant="flushed"
                        required
                      />
                    )}
                  />
                  {errors.period && (
                    <Text color="red.500">{errors.period.message}</Text>
                  )}
                </Flex>
              )}

              <Flex align="center">
                <Text minWidth="120px">Enabled:</Text>
                <Controller
                  name="isEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      isChecked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    >
                      Enabled
                    </Checkbox>
                  )}
                />
                {errors.isEnabled && (
                  <Text color="red.500">{errors.isEnabled.message}</Text>
                )}
              </Flex>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button colorScheme="green" type="submit" disabled={!isValid}>
              {isEditing ? 'Save' : 'Create Device'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ModalComponent;
